import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { AdditionalDriver } from '../../models/booking/AdditionalDriver';
import { formatLocalDate } from '../../utils/time';
import { BookingActivity } from '../../models/booking/ActivitiesResponse';
import { getSelectedActivityDurationHours } from '../../utils/bookingUtils';

const today = formatLocalDate(new Date());

export interface NewBookingContextModel {
  location: string | '';
  activityId: string | null;
  activities: BookingActivity[];
  fullMotionSimulators: number;
  esportsSimulators: number;
  date: string;
  startTime: string;
  endTime: string;
  additionalDrivers: AdditionalDriver[];
  isSimulatorSelected: boolean;
}

export const defaultNewBookingContext: NewBookingContextModel = {
  location: '',
  activityId: null,
  activities: [],
  fullMotionSimulators: 0,
  esportsSimulators: 0,
  date: '',
  startTime: '',
  endTime: '',
  additionalDrivers: [],
  isSimulatorSelected: false
};

interface BookingContextValue {
  booking: NewBookingContextModel;
  durationHours: number;
  updateBooking: (value: Partial<NewBookingContextModel>) => void;
  resetBooking: () => void;
}

const BookingContext = createContext<BookingContextValue | undefined>(undefined);

interface BookingProviderProps {
  children: React.ReactNode;
}

const BOOKING_DRAFT_STORAGE_KEY = 'bookingDraft';

const loadBookingDraft = (): NewBookingContextModel => {
  const raw = localStorage.getItem(BOOKING_DRAFT_STORAGE_KEY);
  if (!raw) return defaultNewBookingContext;
  try {
    const parsed = JSON.parse(raw) as Partial<NewBookingContextModel>;
    parsed.date = parsed.date && parsed.date < today ? today : parsed.date;
    return { ...defaultNewBookingContext, ...parsed };
  } catch {
    return defaultNewBookingContext;
  }
};

export const BookingProvider: React.FC<BookingProviderProps> = ({ children }) => {
  const [booking, setBooking] = useState<NewBookingContextModel>(() => loadBookingDraft());
  const durationHours = getSelectedActivityDurationHours(booking.activities, booking.activityId);

const updateBooking = useCallback((nextValue: Partial<NewBookingContextModel>) => {
        setBooking((current) => {
          const hasSimulators = (nextValue.fullMotionSimulators ?? current.fullMotionSimulators) > 0 || 
                               (nextValue.esportsSimulators ?? current.esportsSimulators) > 0;
          
          const next = { 
            ...current, 
            ...nextValue,
            isSimulatorSelected: hasSimulators
          };
          localStorage.setItem(BOOKING_DRAFT_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);
  const resetBooking = useCallback(() => {
    localStorage.removeItem(BOOKING_DRAFT_STORAGE_KEY);
    setBooking(defaultNewBookingContext);
  }, []);

  const value = useMemo(
    () => ({
      booking,
      durationHours,
      updateBooking,
      resetBooking
    }),
    [booking, durationHours, resetBooking, updateBooking]
  );

  return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>;
};

export const useBookingContext = (): BookingContextValue => {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useBookingWizard must be used within BookingWizardProvider');
  }

  return context;
};
